import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Flex, Form, FormInstance, Input, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../common/constants";
import { OrderDirection } from "../../../../common/enums";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { InstitutionTreeSelect } from "../../../enumerations/components/form/InstitutionTreeSelect";
import { ProductTreeSelect } from "../../../enumerations/components/form/ProductTreeSelect";
import {
  ContractOrderByColumn,
  ContractStatus,
  contractStatusTMap,
  ContractVerificationStatus,
  contractVerificationStatusTMap,
  ContractView,
} from "../../enums";
import { ContractFilterPageRequest } from "../../types";
import { AgentExtendedFilterModal } from "../extendedfilter/AgentExtendedFilterModal";
import { ContractExtendedFilterModal } from "../extendedfilter/ContractExtendedFilterModal";
import ContractStatusTag from "../tags/ContractStatusTag";
import ContractVerificationStatusTag from "../tags/ContractVerificationStatusTag";

type ExtendedContractFilterPageRequest = ContractFilterPageRequest & {
  createdAtRange?: string[];
  verifiedAtRange?: string[];
  mediationReportSignDateRange?: string[];
  effectiveBeginningDateRange?: string[];
  signDateRange?: string[];
  effectiveEndDateOrLoanMaturityDateRange?: string[];
  cancellationDateRange?: string[];
  transferredToOtherBrokerDateRange?: string[];
  vehicleFirstRegistrationYearRange?: number[];
};

type Props = {
  form: FormInstance<ExtendedContractFilterPageRequest>;
  report: ContractView;
  orderBy: ContractOrderByColumn[];
  orderDirections: OrderDirection[];
  validatedSearchParams?: ContractFilterPageRequest;
  updateSearchParams: (searchParams: ContractFilterPageRequest, append?: boolean, replace?: boolean) => void;
};

export const ContractFilterForm = ({
  form,
  validatedSearchParams,
  updateSearchParams,
  report,
  orderDirections,
  orderBy,
}: Props) => {
  const { t } = useTranslation();

  const [isGainerExtendedFilterOpen, setIsGainerExtendedFilterOpen] = useState(false);

  const institutionIds = Form.useWatch("institutionIds", form);

  useEffect(() => {
    if (!validatedSearchParams) {
      return;
    }

    form.setFieldsValue({
      ...validatedSearchParams,
      createdAtRange:
        validatedSearchParams.createdAtMin && validatedSearchParams.createdAtMax
          ? [validatedSearchParams.createdAtMin, validatedSearchParams.createdAtMax]
          : undefined,
      createdAtMin: validatedSearchParams.createdAtMin,
      createdAtMax: validatedSearchParams.createdAtMax,
      verifiedAtRange:
        validatedSearchParams.verifiedAtMin && validatedSearchParams.verifiedAtMax
          ? [validatedSearchParams.verifiedAtMin, validatedSearchParams.verifiedAtMax]
          : undefined,
      verifiedAtMin: validatedSearchParams.verifiedAtMin,
      verifiedAtMax: validatedSearchParams.verifiedAtMax,
      mediationReportSignDateRange:
        validatedSearchParams.mediationReportSignDateMin && validatedSearchParams.mediationReportSignDateMax
          ? [validatedSearchParams.mediationReportSignDateMin, validatedSearchParams.mediationReportSignDateMax]
          : undefined,
      mediationReportSignDateMin: validatedSearchParams.mediationReportSignDateMin,
      mediationReportSignDateMax: validatedSearchParams.mediationReportSignDateMax,
      effectiveBeginningDateRange:
        validatedSearchParams.effectiveBeginningDateMin && validatedSearchParams.effectiveBeginningDateMax
          ? [validatedSearchParams.effectiveBeginningDateMin, validatedSearchParams.effectiveBeginningDateMax]
          : undefined,
      effectiveBeginningDateMin: validatedSearchParams.effectiveBeginningDateMin,
      effectiveBeginningDateMax: validatedSearchParams.effectiveBeginningDateMax,
      signDateRange:
        validatedSearchParams.signDateMin && validatedSearchParams.signDateMax
          ? [validatedSearchParams.signDateMin, validatedSearchParams.signDateMax]
          : undefined,
      signDateMin: validatedSearchParams.signDateMin,
      signDateMax: validatedSearchParams.signDateMax,
      effectiveEndDateOrLoanMaturityDateRange:
        validatedSearchParams.effectiveEndDateOrLoanMaturityDateMin &&
        validatedSearchParams.effectiveEndDateOrLoanMaturityDateMax
          ? [
              validatedSearchParams.effectiveEndDateOrLoanMaturityDateMin,
              validatedSearchParams.effectiveEndDateOrLoanMaturityDateMax,
            ]
          : undefined,
      effectiveEndDateOrLoanMaturityDateMin: validatedSearchParams.effectiveEndDateOrLoanMaturityDateMin,
      effectiveEndDateOrLoanMaturityDateMax: validatedSearchParams.effectiveEndDateOrLoanMaturityDateMax,
      cancellationDateRange:
        validatedSearchParams.cancellationDateMin && validatedSearchParams.cancellationDateMax
          ? [validatedSearchParams.cancellationDateMin, validatedSearchParams.cancellationDateMax]
          : undefined,
      cancellationDateMin: validatedSearchParams.cancellationDateMin,
      cancellationDateMax: validatedSearchParams.cancellationDateMax,
      transferredToOtherBrokerDateRange:
        validatedSearchParams.transferredToOtherBrokerDateMin && validatedSearchParams.transferredToOtherBrokerDateMax
          ? [
              validatedSearchParams.transferredToOtherBrokerDateMin,
              validatedSearchParams.transferredToOtherBrokerDateMax,
            ]
          : undefined,
      transferredToOtherBrokerDateMin: validatedSearchParams.transferredToOtherBrokerDateMin,
      transferredToOtherBrokerDateMax: validatedSearchParams.transferredToOtherBrokerDateMax,
      vehicleFirstRegistrationYearRange:
        validatedSearchParams.vehicleFirstRegistrationYearMin && validatedSearchParams.vehicleFirstRegistrationYearMax
          ? [
              validatedSearchParams.vehicleFirstRegistrationYearMin,
              validatedSearchParams.vehicleFirstRegistrationYearMax,
            ]
          : undefined,
      vehicleFirstRegistrationYearMin: validatedSearchParams.vehicleFirstRegistrationYearMin,
      vehicleFirstRegistrationYearMax: validatedSearchParams.vehicleFirstRegistrationYearMax,
    });
  }, [validatedSearchParams, form]);

  const handleFormSubmit = ({
    createdAtRange,
    verifiedAtRange,
    mediationReportSignDateRange,
    effectiveBeginningDateRange,
    signDateRange,
    effectiveEndDateOrLoanMaturityDateRange,
    cancellationDateRange,
    transferredToOtherBrokerDateRange,
    vehicleFirstRegistrationYearRange,
    ...filter
  }: ExtendedContractFilterPageRequest) => {
    const filterData: ContractFilterPageRequest = {
      ...filter,
      createdAtMin: createdAtRange?.[0],
      createdAtMax: createdAtRange?.[1],
      verifiedAtMin: verifiedAtRange?.[0],
      verifiedAtMax: verifiedAtRange?.[1],
      mediationReportSignDateMin: mediationReportSignDateRange?.[0],
      mediationReportSignDateMax: mediationReportSignDateRange?.[1],
      effectiveBeginningDateMin: effectiveBeginningDateRange?.[0],
      effectiveBeginningDateMax: effectiveBeginningDateRange?.[1],
      signDateMin: signDateRange?.[0],
      signDateMax: signDateRange?.[1],
      effectiveEndDateOrLoanMaturityDateMin: effectiveEndDateOrLoanMaturityDateRange?.[0],
      effectiveEndDateOrLoanMaturityDateMax: effectiveEndDateOrLoanMaturityDateRange?.[1],
      cancellationDateMin: cancellationDateRange?.[0],
      cancellationDateMax: cancellationDateRange?.[1],
      transferredToOtherBrokerDateMin: transferredToOtherBrokerDateRange?.[0],
      transferredToOtherBrokerDateMax: transferredToOtherBrokerDateRange?.[1],
      vehicleFirstRegistrationYearMin: vehicleFirstRegistrationYearRange?.[0],
      vehicleFirstRegistrationYearMax: vehicleFirstRegistrationYearRange?.[1],
      pageIndex: undefined,
    };

    updateSearchParams(filterData, false, true);
  };

  return (
    <Form form={form} name="contractsFilterForm" onFinish={handleFormSubmit} layout="vertical">
      <HiddenInput name="report" initialValue={report} />
      <HiddenInput name="orderBy" initialValue={orderBy} />
      <HiddenInput name="orderDirections" initialValue={orderDirections} />
      <HiddenInput name="pageSize" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(6, 1fr)`,
          gap: rowGutter,
          alignItems: "end",
        }}
        className="min-width-0-children"
      >
        <Form.Item
          name="keyword"
          label={
            <LabelWithPopover
              label={t("contract.filter.keyword")}
              popoverTitle={t("contract.filter.helpers.keywordPopoverTitle")}
              popoverContent={
                <ul>
                  <li>{t("contract.filter.helpers.keywordPopoverSearchBy1")}</li>
                  <li>{t("contract.filter.helpers.keywordPopoverSearchBy2")}</li>
                  <li>{t("contract.filter.helpers.keywordPopoverSearchBy3")}</li>
                  <li>{t("contract.filter.helpers.keywordPopoverSearchBy4")}</li>
                </ul>
              }
            />
          }
          rules={[
            validations.size(
              validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
              validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
            ),
          ]}
        >
          <Input allowClear />
        </Form.Item>

        <Form.Item name="institutionIds" label={t("contract.filter.institutions")}>
          <InstitutionTreeSelect />
        </Form.Item>

        <Form.Item
          name="productIds"
          label={
            <LabelWithTooltip
              tooltip={t("components.productTreeSelect.labelHint")}
              label={t("components.productTreeSelect.label")}
            />
          }
        >
          <ProductTreeSelect institutionIdsFilter={institutionIds} />
        </Form.Item>

        <div style={{ position: "relative" }}>
          <Form.Item name="gainerIds" label={t("contract.filter.gainerIds")}>
            <AgentSelect allowClear isMultiple onExtended={() => setIsGainerExtendedFilterOpen(true)} />
          </Form.Item>

          <AgentExtendedFilterModal
            isOpen={isGainerExtendedFilterOpen}
            onCancel={() => setIsGainerExtendedFilterOpen(false)}
            onSubmit={(values) => {
              form.setFieldsValue(values);
              handleFormSubmit(form.getFieldsValue());
              setIsGainerExtendedFilterOpen(false);
            }}
            fieldNames={{
              allSubordinateIdFieldName: "gainerAllSubordinateId",
              directSubordinateIdFieldName: "gainerDirectSubordinateId",
              directorateIdsFieldName: "gainerDirectorateIds",
            }}
          />
        </div>

        <Form.Item name="statuses" label={t("contract.filter.statuses")}>
          <Select
            {...selectTagsStandardProps(contractStatusTMap)}
            allowClear
            mode="multiple"
            maxTagCount="responsive"
            tagRender={(props) => (
              <ContractStatusTag
                status={props.value}
                closable={props.closable}
                onClose={props.onClose}
                label={props.label}
              />
            )}
            options={Object.values(ContractStatus).map((status) => ({
              value: status,
              label: <ContractStatusTag status={status} />,
            }))}
          />
        </Form.Item>

        <Form.Item name="verificationStatuses" label={t("contract.filter.verificationStatus")}>
          <Select<ContractVerificationStatus[]>
            {...selectTagsStandardProps(contractVerificationStatusTMap)}
            allowClear
            mode="multiple"
            maxTagCount="responsive"
            tagRender={(props) => (
              <ContractVerificationStatusTag
                status={props.value}
                closable={props.closable}
                onClose={props.onClose}
                label={props.label}
              />
            )}
            options={Object.values(ContractVerificationStatus).map((status) => ({
              value: status,
              label: <ContractVerificationStatusTag status={status} style={{ verticalAlign: "top" }} />,
            }))}
          />
        </Form.Item>
      </div>

      <Flex justify="space-between" align="end">
        <ContractExtendedFilterModal
          onSubmit={(values) => {
            form.setFieldsValue(values);
            handleFormSubmit(form.getFieldsValue());
          }}
        />

        <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
          {t("contract.filter.submit")}
        </Button>
      </Flex>
    </Form>
  );
};
